// @flow

import React from "react";
import { localize } from "redux-i18n";
import { connect } from "react-redux";
import { ContactInfoModal } from "@modules/profile/components";
import { ColumnsContainer, SectionComponent } from "@components";
import profileActionCreators from "@modules/profile/actionCreator";
import { getContactInfoModalVisible } from "@modules/profile/selectors";
import type { State } from "@store/reducers";
import type { MyDetails } from "@modules/profile/reducer/my-details/types";
import type { Profile } from "@modules/account/types";
import { formatSecondsAsYearMonths, formatDate, transformSecondsInHours } from "@transformers/date";
import * as profileSelectors from "@modules/profile/selectors";
import KeycloakAdapter from "@src/keycloak";

type OwnProps = $Exact<{
  contactInfoSchema: Object | null,
  myDetails: MyDetails,
  profile: Profile,
}>;

type PropsFromState = $Exact<{
  isContactInfoModalVisible: boolean,
}>;

type PropsFromDispatch = $Exact<{
  onShowContactInfoModalHandler: Function,
  onDismissContactInfoModalHandler: Function,
  updateContactInfo: Function,
}>;

type Props = $Exact<{
  ...PropsFromState,
  ...PropsFromDispatch,
  ...OwnProps,
  ...PropsFromLocalize,
}>;

export class MyInformation extends React.Component<Props> {
  handleChangePassword = () => {
    KeycloakAdapter.changePassword();
  };

  generateColumns(data: Object) {
    const employmentInfo = data.employmentInfo || {};
    let organisationalUnits = this.props.t("N/A");
    if (
      employmentInfo &&
      employmentInfo.organisationalUnits &&
      employmentInfo.organisationalUnits.length > 0
    ) {
      organisationalUnits = employmentInfo.organisationalUnits.join(", ");
    }
    let jobTitles = this.props.t("N/A");
    if (employmentInfo && employmentInfo.jobTitles && employmentInfo.jobTitles.length > 0) {
      jobTitles = employmentInfo.jobTitles.join(", ");
    }

    const contactInfo = data.contactInfo || {};

    return [
      {
        columns: [
          {
            columnTitle: this.props.t("PERSONAL_DETAILS"),
            data: [
              [
                this.props.t("EMPLOYEE_NAME"),
                (data.personalDetails || {}).name || this.props.t("N/A"),
              ],
              [
                this.props.t("DATE_OF_BIRTH"),
                (data.personalDetails || {}).dateOfBirth
                  ? formatDate(data.personalDetails.dateOfBirth)
                  : this.props.t("N/A"),
              ],
            ],
          },
          {
            columnTitle: this.props.t("EMPLOYEMENT_INFO"),
            data: [
              [
                this.props.t("EMPLOYEE_REF"),
                employmentInfo.employeeReference || this.props.t("N/A"),
              ],
              [this.props.t("DEPARTMENT"), organisationalUnits],
              [this.props.t("JOB_TITLE"), jobTitles],
              [
                this.props.t("EMPLOYMENT_START"),
                employmentInfo.employmentStartDate
                  ? formatDate(data.employmentInfo.employmentStartDate)
                  : this.props.t("N/A"),
              ],
              [
                this.props.t("SERVICE_LENGTH"),
                formatSecondsAsYearMonths(employmentInfo.serviceLengthInSeconds || 0),
              ],
              [
                this.props.t("CONTRACTED_HOURS"),
                employmentInfo.weeklyContractedHoursInSeconds
                  ? transformSecondsInHours(employmentInfo.weeklyContractedHoursInSeconds)
                  : this.props.t("N/A"),
              ],
              [
                this.props.t("ANNUAL_WORKING_DAYS_AVAILABLE"),
                employmentInfo.daysPerYear || this.props.t("N/A"),
              ],
            ],
          },
          {
            columnTitle: "Contact Info",
            columnButtons: [
              {
                text: this.props.t("EDIT"),
                materialIcon: "edit",
                onClick: this.props.onShowContactInfoModalHandler,
              },
            ],
            data: [
              [
                this.props.t("SECONDARY_EMAIL_ADDRESS"),
                contactInfo.personalEmail || this.props.t("N/A"),
              ],
              [this.props.t("PRIMARY_EMAIL_ADDRESS"), contactInfo.workEmail || this.props.t("N/A")],
              [this.props.t("HOME_PHONE"), contactInfo.homePhone || this.props.t("N/A")],
              [this.props.t("MOBILE_PHONE"), contactInfo.mobilePhone || this.props.t("N/A")],
              [this.props.t("WORK_PHONE"), contactInfo.workPhone || this.props.t("N/A")],
            ],
          },
          {
            columnTitle: this.props.t("ACCOUNT_DETAILS"),
            columnButtons: [
              {
                text: this.props.t("CHANGE_PASSWORD"),
                onClick: this.handleChangePassword,
              },
            ],
            data: [
              [
                this.props.t("ACCESS_LEVEL"),
                (this.props.profile || {}).user
                  ? this.props.t((this.props.profile || {}).user.userType)
                  : "",
              ],
              [
                this.props.t("DATE_CREATED"),
                (data.personalDetails || {}).createdDate
                  ? formatDate((data.personalDetails || {}).createdDate)
                  : this.props.t("N/A"),
              ],
              [
                this.props.t("DATE_REGISTERED"),
                (data.personalDetails || {}).registeredDate
                  ? formatDate((data.personalDetails || {}).registeredDate)
                  : this.props.t("N/A"),
              ],
            ],
          },
        ],
      },
    ];
  }

  updateContactInfo = (formData: Object) => {
    this.props.updateContactInfo(formData);
  };

  changePassword = (formData: Object) => {
    this.props.changePassword(formData);
  };

  render() {
    const contactInfo = (this.props.myDetails || {}).contactInfo || {};
    return (
      <React.Fragment>
        <SectionComponent wrapperClassName="flex-column">
          <ColumnsContainer
            rows={this.generateColumns({ ...this.props.myDetails, ...this.props.profile })}
          />
        </SectionComponent>
        {this.props.isContactInfoModalVisible && (
          <ContactInfoModal
            onSubmit={this.updateContactInfo}
            onDismissHandler={this.props.onDismissContactInfoModalHandler}
            contactInfo={{
              homePhone: contactInfo.homePhone,
              personalEmail: contactInfo.personalEmail,
              mobilePhone: contactInfo.mobilePhone,
            }}
            schema={this.props.contactInfoSchema}
          />
        )}
      </React.Fragment>
    );
  }
}
const mapStateToProps = (state: State): PropsFromState => {
  return {
    isContactInfoModalVisible: getContactInfoModalVisible(state),
  };
};

const mapDispatchToProps: PropsFromDispatch = {
  onShowContactInfoModalHandler: () => profileActionCreators.ToggleContactInfoModal(true),
  onDismissContactInfoModalHandler: () => profileActionCreators.ToggleContactInfoModal(false),
  updateContactInfo: profileActionCreators.UpdateContactInfo,
};

export default connect(mapStateToProps, mapDispatchToProps)(localize()(MyInformation));
